import { UpdateProfileStatus, useCustomerDetails, useUpdateSuitability } from '@egw/api'
import { Button, Field, Input, ModalDrawer, RadioGroup, useModalState } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { Controller, useForm } from 'react-hook-form'
import { type FC, useEffect, useState } from 'react'
import { InformationCircleIcon } from '@heroicons/react/16/solid'
import { SuitabilityField } from '../../types'

const log = new Logger('Major Share Holder')

interface SuitabilityFormInterface {
  [SuitabilityField.IS_DIRECTOR_POLICY_MAKER]: boolean
  [SuitabilityField.directorPolicyMakerOfCompany]?: string
}

export const EditExecutiveOrShareholder: FC = () => {
  const { t } = useTranslation('features-settings')
  const { isOpen, openModal, closeModal } = useModalState()

  const [updateError, setUpdateError] = useState<boolean>(false)

  const { data, refetch, isRefetching } = useCustomerDetails()

  const isDirector = data?.customer.isDirectorPolicyMakerOfCompany
  const companyName = data?.customer.directorPolicyMakerOfCompany

  const { mutate: updateSuitability, isLoading } = useUpdateSuitability({
    onSuccess: (res) => {
      if (res.__typename === 'UpdateProfileError' || res.status === UpdateProfileStatus.FAILED) {
        log.error('Error updating Executive Field', { data })
        setUpdateError(true)
      } else if (res.status === UpdateProfileStatus.COMPLETE) {
        void refetch()
          .catch((error: unknown) => {
            log.error(error)
          })
          .finally(() => {
            setUpdateError(false)
            closeModal()
            reset()
          })
      }
    },
    onError: () => {
      setUpdateError(true)
    },
  })

  const defaultValues = {
    [SuitabilityField.IS_DIRECTOR_POLICY_MAKER]: isDirector,
    [SuitabilityField.directorPolicyMakerOfCompany]: companyName,
  }

  const { handleSubmit, control, reset, watch, formState } = useForm<SuitabilityFormInterface>({
    mode: 'all',
    defaultValues,
  })

  const isDirectorPolicyMaker = watch(SuitabilityField.IS_DIRECTOR_POLICY_MAKER)

  useEffect(() => {
    if (data) {
      reset({
        [SuitabilityField.IS_DIRECTOR_POLICY_MAKER]: isDirector,
        [SuitabilityField.directorPolicyMakerOfCompany]: companyName,
      })
    }
  }, [companyName, data, reset, isDirector])

  const onSubmit = async (formData: SuitabilityFormInterface): Promise<void> => {
    setUpdateError(false)
    log.debug('Submitting Form', { formData })
    await updateSuitability({
      [SuitabilityField.IS_DIRECTOR_POLICY_MAKER]: Boolean(isDirectorPolicyMaker),
      ...(isDirectorPolicyMaker
        ? { directorPolicyMakerOfCompany: formData[SuitabilityField.directorPolicyMakerOfCompany] }
        : {}),
    })
  }

  return isDirector !== undefined ? (
    <>
      <Field
        valueClassName="font-light"
        className="w-full justify-between border-list mb-0 py-4 px-2"
        onEdit={openModal}
        value={t('employment_labels.executive_or_share_holder')}
        rightValue={t(`executive_or_share_holder.${String(isDirector)}`)}
      />
      <ModalDrawer title={t('update_major_share_holder.title')} isOpen={isOpen} onClose={closeModal}>
        <form
          id="updateSuitabilityForm"
          className="flex flex-col h-full md:h-screen justify-between"
          onSubmit={(e) => {
            e.preventDefault()
            void handleSubmit(onSubmit)()
          }}
        >
          <div className="flex flex-col">
            <div>
              <p className="text-text-body typo-primary-base-regular mb-4">
                {t('update_major_share_holder.description')}
              </p>
              <Controller
                name={SuitabilityField.IS_DIRECTOR_POLICY_MAKER}
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    {...field}
                    value={Number(field.value)}
                    className="my-xs gap-sm"
                    options={[
                      {
                        value: 0,
                        label: 'No',
                      },
                      {
                        value: 1,
                        label: 'Yes',
                      },
                    ]}
                  />
                )}
              />
            </div>
            {isDirectorPolicyMaker ? (
              <>
                <p className="text-text-body typo-primary-base-regular mb-sm">
                  {t('update_major_share_holder.if_yes')}
                </p>
                <Controller
                  name={SuitabilityField.directorPolicyMakerOfCompany}
                  control={control}
                  render={({ field }) => <Input {...field} placeholder={t('update_major_share_holder.placeholder')} />}
                />
              </>
            ) : null}
            {updateError ? (
              <div className="flex bg-alertErrorBg p-3 rounded-md">
                <InformationCircleIcon className="w-6 h-6 fill-red-500" />
                <p className="text-sm text-alertErrorText ml-2">{t('common.update_error')} </p>
              </div>
            ) : null}
          </div>
          <Button
            id="updateBrokerageAffiliation"
            type="submit"
            isLoading={isLoading || isRefetching}
            isDisabled={!formState.isValid}
            className="mt-4 md:mt-0"
          >
            {t('common.confirm')}
          </Button>
        </form>
      </ModalDrawer>
    </>
  ) : null
}
